export type RevisionType =
  | 'INCREMENT' | 'PROMOTION' | 'MARKET_ADJUSTMENT' | 'CORRECTION' | 'BONUS'

export type RevisionStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'APPLIED' | 'CANCELLED'

export type PlanType = 'MEDICAL' | 'LIFE' | 'ACCIDENT' | 'ALLOWANCE' | 'PENSION' | 'OTHER'
export type CostBasis = 'FIXED_PER_MONTH' | 'PERCENT_OF_BASIC' | 'PER_DEPENDANT' | 'NONE'
export type Relationship = 'SPOUSE' | 'CHILD' | 'PARENT' | 'OTHER'

export const REVISION_TYPE_LABELS: Record<RevisionType, string> = {
  INCREMENT: 'Annual increment',
  PROMOTION: 'Promotion',
  MARKET_ADJUSTMENT: 'Market adjustment',
  CORRECTION: 'Correction',
  BONUS: 'Bonus',
}

export const REVISION_STATUS_LABELS: Record<RevisionStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Awaiting approval',
  APPROVED: 'Approved, not yet applied',
  REJECTED: 'Rejected',
  APPLIED: 'Applied to pay',
  CANCELLED: 'Cancelled',
}

export const PLAN_TYPE_LABELS: Record<PlanType, string> = {
  MEDICAL: 'Medical',
  LIFE: 'Life assurance',
  ACCIDENT: 'Personal accident',
  ALLOWANCE: 'Allowance',
  PENSION: 'Pension',
  OTHER: 'Other',
}

/**
 * How a plan is priced.
 *
 * The wording says what has to be filled in, because a basis with no figure behind it silently
 * charges nothing — which reads on a screen as a free benefit.
 */
export const COST_BASIS_LABELS: Record<CostBasis, string> = {
  FIXED_PER_MONTH: 'A fixed amount each month',
  PERCENT_OF_BASIC: 'A percentage of basic pay',
  PER_DEPENDANT: 'A base amount plus a charge for each dependant',
  NONE: 'Free — the employer bears all of it',
}

export const RELATIONSHIP_LABELS: Record<Relationship, string> = {
  SPOUSE: 'Spouse',
  CHILD: 'Child',
  PARENT: 'Parent',
  OTHER: 'Other',
}

export interface PayScaleView {
  id: string
  legalEntityId: string | null
  gradeId: string
  gradeCode: string | null
  gradeName: string | null
  effectiveFrom: string
  effectiveTo: string | null
  minimum: number
  midpoint: number
  maximum: number
  currency: string
  notes: string | null
}

export interface SalaryRevisionView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  revisionType: string
  effectiveFrom: string
  elementId: string | null
  elementCode: string | null
  currentAmount: number | null
  proposedAmount: number
  /** Null when there was nothing to rise from. */
  increasePercent: number | null
  currency: string | null
  gradeId: string | null
  /** The midpoint when the decision was taken, so the ratio behind it stays true. */
  scaleMidpoint: number | null
  compaRatioAfter: number | null
  reason: string | null
  status: string
  submittedAt: string | null
  decidedAt: string | null
  decidedBy: string | null
  decisionComment: string | null
  appliedAt: string | null
}

export interface CompaRatioView {
  employeeId: string
  employeeName: string | null
  gradeId: string | null
  gradeName: string | null
  salary: number | null
  minimum: number | null
  midpoint: number | null
  maximum: number | null
  /** Null, never zero, when no band applies — the two are different facts. */
  compaRatio: number | null
  insideBand: boolean
  unavailableReason: string | null
}

export interface ElementGlMappingView {
  id: string
  legalEntityId: string | null
  elementId: string
  elementCode: string | null
  elementName: string | null
  effectiveFrom: string
  effectiveTo: string | null
  debitAccount: string
  creditAccount: string
  costCentreSource: string
  notes: string | null
}

export interface ControlAccountView {
  id: string
  legalEntityId: string | null
  netPayAccount: string
  roundingAccount: string | null
  notes: string | null
}

export interface BenefitPlanView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  planType: string
  provider: string | null
  policyNumber: string | null
  costBasis: string
  employeeCost: number | null
  employerCost: number | null
  costPerDependant: number | null
  percentOfBasic: number | null
  currency: string | null
  coversDependants: boolean
  maxDependants: number | null
  enrolmentOpens: string | null
  enrolmentCloses: string | null
  effectiveFrom: string
  effectiveTo: string | null
  active: boolean
  enrolledCount: number
}

export interface BenefitDependantView {
  id: string
  fullName: string
  relationship: string
  dateOfBirth: string | null
}

export interface BenefitEnrolmentView {
  id: string
  employeeId: string
  employeeName: string | null
  planId: string
  planName: string | null
  effectiveFrom: string
  effectiveTo: string | null
  status: string
  /** Snapshotted at enrolment; the plan's price may have moved since. */
  employeeCost: number | null
  employerCost: number | null
  dependantCount: number
  dependants: BenefitDependantView[]
}

/** Where a compa-ratio sits, in words rather than only as a number. */
export function compaRatioVerdict(ratio: number | null): string | null {
  if (ratio == null) return null
  if (ratio < 80) return 'well below the band midpoint'
  if (ratio < 95) return 'below the midpoint'
  if (ratio <= 105) return 'around the midpoint'
  if (ratio <= 120) return 'above the midpoint'
  return 'well above the midpoint'
}
